import { getGoogleMapsDistanceViaEdge } from './googleMapsEdge';

type Direction = 'N' | 'S' | 'E' | 'W';

export interface RadarData {
    active: boolean;
    incidents: any[];
    trafficIndex: number;
    congestionLevel: 'normal' | 'moderate' | 'high' | 'critical';
    details: Record<Direction, number>;
    lastScan: number;
    lastUpdated: number;
}

const RADAR_DISTANCE_KM = 2;
const KM_TO_LAT = 1 / 111;

const kmToLng = (km: number, lat: number): number => {
    const latitudeFactor = Math.cos((lat * Math.PI) / 180) || 1;
    return km / (111 * latitudeFactor);
};

const getDestinationForDirection = (lat: number, lng: number, direction: Direction) => {
    const latOffset = RADAR_DISTANCE_KM * KM_TO_LAT;
    const lngOffset = kmToLng(RADAR_DISTANCE_KM, lat);

    if (direction === 'N') return { lat: lat + latOffset, lng };
    if (direction === 'S') return { lat: lat - latOffset, lng };
    if (direction === 'E') return { lat, lng: lng + lngOffset };
    return { lat, lng: lng - lngOffset };
};

const clampFactor = (value: number): number => {
    if (!Number.isFinite(value)) return 1;
    return Math.max(0.85, Math.min(3, value));
};

const resolveCongestionLevel = (index: number): RadarData['congestionLevel'] => {
    if (index >= 1.45) return 'critical';
    if (index >= 1.25) return 'high';
    if (index >= 1.1) return 'moderate';
    return 'normal';
};

const canUseBrowserDistanceMatrix = (): boolean => {
    if (typeof window === 'undefined') return false;
    const googleRef = (window as any).google;
    return Boolean(
        googleRef?.maps?.DistanceMatrixService &&
        googleRef?.maps?.TravelMode?.DRIVING
    );
};

const getDirectionalCoordinates = (lat: number, lng: number): Record<Direction, { lat: number; lng: number }> => {
    return {
        N: getDestinationForDirection(lat, lng, 'N'),
        S: getDestinationForDirection(lat, lng, 'S'),
        E: getDestinationForDirection(lat, lng, 'E'),
        W: getDestinationForDirection(lat, lng, 'W')
    };
};

const parseDirectionFactor = (element: any): number => {
    const durationInTraffic = element?.duration_in_traffic?.value;
    const baseDuration = element?.duration?.value;
    if (!durationInTraffic || !baseDuration) return 1;
    return clampFactor(durationInTraffic / Math.max(1, baseDuration));
};

const scanTrafficWithBrowserMatrix = async (lat: number, lng: number): Promise<Record<Direction, number> | null> => {
    if (!canUseBrowserDistanceMatrix()) return null;

    const googleRef = (window as any).google;
    const directional = getDirectionalCoordinates(lat, lng);
    const destinationList = [directional.N, directional.S, directional.E, directional.W];

    const matrix = await new Promise<any>((resolve, reject) => {
        const service = new googleRef.maps.DistanceMatrixService();
        service.getDistanceMatrix(
            {
                origins: [{ lat, lng }],
                destinations: destinationList,
                travelMode: googleRef.maps.TravelMode.DRIVING,
                drivingOptions: {
                    departureTime: new Date(),
                    trafficModel: 'bestguess'
                }
            },
            (response: any, status: any) => {
                if (status !== 'OK' || !response) {
                    reject(new Error('DistanceMatrix unavailable'));
                    return;
                }
                resolve(response);
            }
        );
    }).catch(() => null);

    const elements = matrix?.rows?.[0]?.elements;
    if (!elements || elements.length < 4) return null;

    return {
        N: parseDirectionFactor(elements[0]),
        S: parseDirectionFactor(elements[1]),
        E: parseDirectionFactor(elements[2]),
        W: parseDirectionFactor(elements[3])
    };
};

const scanDirection = async (lat: number, lng: number, apiKey: string, direction: Direction): Promise<number> => {
    const destination = getDestinationForDirection(lat, lng, direction);
    const route = await getGoogleMapsDistanceViaEdge(lat, lng, destination.lat, destination.lng, apiKey);

    if (!route || !route.durationMins || !route.durationNoTrafficMins) return 1;
    const rawFactor = route.durationMins / Math.max(1, route.durationNoTrafficMins);
    return clampFactor(rawFactor);
};

export const scanTrafficRadar = async (lat?: number, lng?: number, apiKey?: string): Promise<RadarData | null> => {
    if (!lat || !lng) return null;

    try {
        const browserDetails = await scanTrafficWithBrowserMatrix(lat, lng);

        const details = browserDetails || (apiKey
            ? {
                N: await scanDirection(lat, lng, apiKey, 'N'),
                S: await scanDirection(lat, lng, apiKey, 'S'),
                E: await scanDirection(lat, lng, apiKey, 'E'),
                W: await scanDirection(lat, lng, apiKey, 'W')
            }
            : null);

        if (!details) return null;

        const averageFactor = (details.N + details.S + details.E + details.W) / 4;
        const now = Date.now();

        return {
            active: true,
            incidents: [],
            trafficIndex: Number(averageFactor.toFixed(2)),
            congestionLevel: resolveCongestionLevel(averageFactor),
            details,
            lastScan: now,
            lastUpdated: now
        };
    } catch (_error) {
        return null;
    }
};
